import { db } from '../firebase/config';
import { COLLECTIONS } from '../types/firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getPlanById, SubscriptionPlan } from '../constants/Subscriptions';
import type { CompanySubscriptionFromFirestore } from './stripeWorkerClient';

export interface UserLimitValidationResult {
  canCreateUser: boolean;
  currentUserCount: number;
  maxUsers: number;
  isUnlimited: boolean;
  errorMessage?: string;
}

export interface CompanySubscriptionData {
  subscriptionPlan?: {
    id?: string;
    sku?: string;
    title?: string;
    details?: string;
    available?: boolean;
    /** Worker / portal may persist purchased seat cap (overrides catalog maxUsers). */
    maxUsers?: number;
    maxSites?: number;
    maxReports?: number;
    category?: 'law' | 'corporate';
  };
  subscription?: CompanySubscriptionFromFirestore | null;
}

function resolvePlanFromStored(
  subscriptionPlan: NonNullable<CompanySubscriptionData['subscriptionPlan']>,
  stripeCategory?: 'law' | 'corporate'
): SubscriptionPlan | undefined {
  if (subscriptionPlan.id) {
    const byId = getPlanById(subscriptionPlan.id);
    if (byId) return byId;
  }
  const cat = subscriptionPlan.category ?? stripeCategory;
  const sku = (subscriptionPlan.sku || '').toLowerCase();
  if (cat === 'law') {
    if (sku === 'basic') return getPlanById('LEO-Basic');
    if (sku === 'ultra') return getPlanById('LEO-Ultra');
    if (sku === 'enterprise') return getPlanById('LEO-Enterprise');
  }
  if (sku === 'basic') return getPlanById('Corp-Basic');
  if (sku === 'premium') return getPlanById('Corp-Premium');
  if (sku === 'ultra') return getPlanById('Corp-Ultra');
  if (sku === 'enterprise') return getPlanById('Corp-Enterprise');
  return undefined;
}

/** Firestore / Stripe may store a higher seat count than the static catalog tier. */
function applyPurchasedSeatCap(
  plan: SubscriptionPlan,
  raw: CompanySubscriptionData['subscriptionPlan'] | undefined,
  stripeSub: CompanySubscriptionFromFirestore | null | undefined
): SubscriptionPlan {
  const fromDoc =
    raw && typeof raw.maxUsers === 'number' && raw.maxUsers > 0 ? Math.floor(raw.maxUsers) : undefined;
  const fromStripe =
    stripeSub && typeof stripeSub.quantity === 'number' && stripeSub.quantity > 0
      ? Math.floor(stripeSub.quantity)
      : undefined;
  const cap = fromDoc ?? fromStripe;
  if (cap == null) return plan;
  return { ...plan, maxUsers: cap };
}

/**
 * Get the current subscription plan for a company
 */
export const getCompanySubscriptionPlan = async (companyId: string): Promise<SubscriptionPlan | null> => {
  try {
    const companyDoc = await getDoc(doc(db, COLLECTIONS.COMPANIES, companyId));

    if (!companyDoc.exists()) {
      console.error('Company document not found');
      return null;
    }

    const companyData = companyDoc.data() as CompanySubscriptionData;
    const subscriptionPlan = companyData.subscriptionPlan;
    const stripeSub = companyData.subscription ?? undefined;

    if (!subscriptionPlan) {
      const basic = getPlanById('Corp-Basic');
      return basic ? applyPurchasedSeatCap(basic, undefined, stripeSub) : null;
    }

    let fullPlan = resolvePlanFromStored(subscriptionPlan, stripeSub?.category);

    if (fullPlan) {
      return applyPurchasedSeatCap(fullPlan, subscriptionPlan, stripeSub);
    }

    const cat = subscriptionPlan.category ?? stripeSub?.category;
    const category: SubscriptionPlan['category'] = cat === 'law' ? 'law-enforcement' : 'corporate';

    const seatCap =
      (typeof subscriptionPlan.maxUsers === 'number' && subscriptionPlan.maxUsers > 0
        ? Math.floor(subscriptionPlan.maxUsers)
        : undefined) ??
      (stripeSub && typeof stripeSub.quantity === 'number' && stripeSub.quantity > 0
        ? Math.floor(stripeSub.quantity)
        : undefined) ??
      5;

    return {
      id: subscriptionPlan.id && typeof subscriptionPlan.id === 'string' ? subscriptionPlan.id : 'Corp-Basic',
      title: subscriptionPlan.title || 'Basic Plan',
      description: 'Plan from your subscription record',
      price: 20,
      annualPrice: 200,
      currency: 'USD',
      interval: 'month',
      category,
      maxSites:
        typeof subscriptionPlan.maxSites === 'number' && subscriptionPlan.maxSites >= 0
          ? subscriptionPlan.maxSites
          : category === 'law-enforcement'
            ? 0
            : 2,
      maxReports:
        typeof subscriptionPlan.maxReports === 'number' && subscriptionPlan.maxReports > 0
          ? subscriptionPlan.maxReports
          : 50,
      maxUsers: seatCap,
      features: [subscriptionPlan.details || 'Subscription features'],
      isAvailable: subscriptionPlan.available ?? true,
      isBeta: false,
    } as SubscriptionPlan;
  } catch (error) {
    console.error('Error fetching company subscription plan:', error);
    return null;
  }
};

/**
 * Get current user count for a company
 */
export const getCurrentUserCount = async (masterOwnerID: string): Promise<number> => {
  try {
    const usersQuery = query(
      collection(db, COLLECTIONS.USERS),
      where('masterOwnerID', '==', masterOwnerID)
    );
    const snapshot = await getDocs(usersQuery);
    return snapshot.size;
  } catch (error) {
    console.error('Error fetching user count:', error);
    return 0;
  }
};

/**
 * Validate if a new user can be created based on subscription limits
 */
export const validateUserLimit = async (
  masterOwnerID: string,
  companyId?: string
): Promise<UserLimitValidationResult> => {
  try {
    // Get current user count
    const currentUserCount = await getCurrentUserCount(masterOwnerID);
    
    // Get subscription plan (use masterOwnerID as companyId if not provided)
    const subscriptionPlan = await getCompanySubscriptionPlan(companyId || masterOwnerID);
    
    if (!subscriptionPlan) {
      return {
        canCreateUser: false,
        currentUserCount,
        maxUsers: 5,
        isUnlimited: false,
        errorMessage: 'Unable to determine subscription plan limits'
      };
    }

    // Check if plan has unlimited users
    const isUnlimited = !subscriptionPlan.maxUsers || 
                       subscriptionPlan.id === 'Corp-Enterprise' || 
                       subscriptionPlan.id === 'LEO-Enterprise';
    
    if (isUnlimited) {
      return {
        canCreateUser: true,
        currentUserCount,
        maxUsers: -1, // -1 indicates unlimited
        isUnlimited: true
      };
    }

    const maxUsers = subscriptionPlan.maxUsers || 5;
    const canCreateUser = currentUserCount < maxUsers;
    
    return {
      canCreateUser,
      currentUserCount,
      maxUsers,
      isUnlimited: false,
      errorMessage: canCreateUser ? undefined : 
        `User limit reached. Your ${subscriptionPlan.title} plan allows up to ${maxUsers} user${maxUsers === 1 ? '' : 's'}. Please upgrade your plan to add more users.`
    };
  } catch (error) {
    console.error('Error validating user limit:', error);
    return {
      canCreateUser: false,
      currentUserCount: 0,
      maxUsers: 1,
      isUnlimited: false,
      errorMessage: 'Error checking user limits. Please try again.'
    };
  }
};

/**
 * Get subscription plan limits for display purposes
 */
export const getSubscriptionLimits = async (companyId: string) => {
  try {
    const plan = await getCompanySubscriptionPlan(companyId);
    
    if (!plan) {
      // Default to Corporate Basic Plan limits
      return {
        users: 5,
        sites: 2,
        reports: 50,
        isUnlimited: false,
        unlimitedReports: false,
      };
    }

    const isUnlimited = plan.id === 'Corp-Enterprise' || plan.id === 'LEO-Enterprise';
    const unlimitedReports =
      isUnlimited || plan.maxReports == null;

    return {
      users: plan.maxUsers ?? 5,
      sites: plan.maxSites ?? (plan.category === 'law-enforcement' ? 0 : 2),
      reports: unlimitedReports ? -1 : (plan.maxReports as number),
      isUnlimited,
      unlimitedReports,
    };
  } catch (error) {
    console.error('Error getting subscription limits:', error);
    // Default to Corporate Basic Plan limits
    return {
      users: 5,
      sites: 2,
      reports: 50,
      isUnlimited: false,
      unlimitedReports: false,
    };
  }
};

/**
 * Check if user creation should be disabled based on limits
 */
export const shouldDisableUserCreation = async (masterOwnerID: string, companyId?: string): Promise<boolean> => {
  const validation = await validateUserLimit(masterOwnerID, companyId);
  return !validation.canCreateUser;
};

/**
 * Switch/upgrade a company's subscription plan
 */
export const switchSubscriptionPlan = async (
  companyId: string, 
  newPlan: SubscriptionPlan,
  isAnnual: boolean = false
): Promise<void> => {
  try {
    const { doc, updateDoc } = await import('firebase/firestore');
    
    const subscriptionData = {
      id: newPlan.id,
      sku: newPlan.id.toLowerCase().includes('basic') ? 'basic' : 
           newPlan.id.toLowerCase().includes('premium') ? 'premium' : 
           newPlan.id.toLowerCase().includes('ultra') ? 'ultra' : 
           newPlan.id.toLowerCase().includes('enterprise') ? 'enterprise' : 'basic',
      title: newPlan.title,
      details: newPlan.description,
      available: newPlan.isAvailable,
      price: isAnnual && newPlan.annualPrice ? newPlan.annualPrice : newPlan.price,
      billing: isAnnual ? 'annual' : 'monthly',
      category: newPlan.category,
      maxUsers: newPlan.maxUsers,
      maxSites: newPlan.maxSites,
      maxReports: newPlan.maxReports,
      historyRetention: newPlan.historyRetention,
      hasGeoLocation: newPlan.hasGeoLocation,
      features: newPlan.features,
      updatedAt: new Date().toISOString()
    };
    
    const companyRef = doc(db, COLLECTIONS.COMPANIES, companyId);
    await updateDoc(companyRef, {
      subscriptionPlan: subscriptionData
    });
    
  } catch (error) {
    console.error('Error switching subscription plan:', error);
    throw new Error('Failed to switch subscription plan');
  }
};

/** Persist Stripe linkage from enterprise code redemption (worker may create subscription server-side). */
export const mergeCompanyStripeSubscription = async (
  companyId: string,
  subscription: CompanySubscriptionFromFirestore
): Promise<void> => {
  const companyRef = doc(db, COLLECTIONS.COMPANIES, companyId);
  await updateDoc(companyRef, { subscription });
};

/**
 * Get the current plan with full details for a company
 */
export const getCurrentPlan = async (companyId: string): Promise<SubscriptionPlan | null> => {
  return await getCompanySubscriptionPlan(companyId);
};

/**
 * Validate if a plan switch is allowed
 */
export const validatePlanSwitch = async (
  companyId: string, 
  newPlan: SubscriptionPlan
): Promise<{ canSwitch: boolean; reason?: string }> => {
  try {
    const currentPlan = await getCompanySubscriptionPlan(companyId);
    
    if (!currentPlan) {
      return { canSwitch: true }; // Can switch if no current plan
    }
    
    // Check if it's the same plan
    if (currentPlan.id === newPlan.id) {
      return { canSwitch: false, reason: 'Already on this plan' };
    }
    
    // Get current usage
    const currentUserCount = await getCurrentUserCount(companyId);
    
    // Check if new plan can accommodate current usage
    if (newPlan.maxUsers && currentUserCount > newPlan.maxUsers) {
      return { 
        canSwitch: false, 
        reason: `Current user count (${currentUserCount}) exceeds new plan limit (${newPlan.maxUsers})` 
      };
    }
    
    // Add more validation rules as needed
    // For example: check current sites, reports, etc.
    
    return { canSwitch: true };
  } catch (error) {
    console.error('Error validating plan switch:', error);
    return { canSwitch: false, reason: 'Unable to validate plan switch' };
  }
};
